import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Users, CheckCircle, Building2, MapPin, UserCheck, UserX, Lock, Unlock, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

const getScoreColor = (s) => !s ? "text-[#888]" : s >= 70 ? "text-emerald-600" : s >= 40 ? "text-gold-dark" : "text-[#888]";
const getScoreBg = (s) => !s ? "border-cream-warm bg-cream" : s >= 70 ? "border-emerald-200 bg-emerald-500/10" : s >= 40 ? "border-cream-warm bg-gold/10" : "border-cream-warm bg-cream";

const statusColors = {
  interested: "bg-amber-100 text-amber-700 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-600 border-red-200",
  terms_issued: "bg-violet-100 text-violet-700 border-violet-200",
  selected: "bg-emerald-200 text-emerald-800 border-emerald-300",
};
const statusLabels = {
  interested: "Pending Review",
  accepted: "Accepted",
  rejected: "Declined",
  terms_issued: "Terms Issued",
  selected: "Selected",
};

export function LenderMatchesTab({ leadId }) {
  const [matches, setMatches] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [activeView, setActiveView] = useState("interests");

  const fetchInterests = () => {
    return axios.get(`${API}/deals/${leadId}/pending-interests`)
      .then(r => setInterests(r.data.interests || []))
      .catch(console.error);
  };

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/deals/${leadId}/lender-matches`).then(r => setMatches(r.data.matches || [])).catch(() => {}),
      fetchInterests(),
    ]).finally(() => setLoading(false));
  }, [leadId]);

  const handleRespond = async (interestId, action) => {
    setResponding(interestId);
    try {
      await axios.post(`${API}/deals/${leadId}/interest/${interestId}/respond`, { action });
      toast.success(action === "accept" ? "Lender access granted" : "Interest declined");
      await fetchInterests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to respond");
    } finally { setResponding(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>;

  const pendingInterests = interests.filter(i => i.status === "interested");
  const acceptedInterests = interests.filter(i => ["accepted", "terms_issued", "selected"].includes(i.status));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="lender-matches-tab">
      {/* Tab toggle */}
      <div className="flex gap-1.5 mb-2">
        {[
          { id: "interests", label: `Lender Interest${interests.length ? ` (${interests.length})` : ""}` },
          { id: "matches", label: `AI Matches${matches.length ? ` (${matches.length})` : ""}` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveView(tab.id)} data-testid={`lender-tab-${tab.id}`}
            className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeView === tab.id ? "bg-[#022c22] text-[#e8d5b0]" : "text-[#888] hover:text-[#555] hover:bg-[#faf7f2]"
            }`}>
            {tab.label}
            {tab.id === "interests" && pendingInterests.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[8px] bg-amber-500 text-white">{pendingInterests.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Interests View */}
      {activeView === "interests" && (
        <>
          {/* Pending Interests (action required) */}
          {pendingInterests.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Pending Approval</span>
              </div>
              {pendingInterests.map((interest, i) => (
                <motion.div key={interest.interest_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`pending-interest-${interest.interest_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Lock className="h-3.5 w-3.5 text-amber-500" />
                        <span className="font-medium text-[#0A0A0A] text-sm">{interest.lender_name}</span>
                        {interest.lender_company && (
                          <span className="text-xs text-[#999] flex items-center gap-1"><Building2 className="h-3 w-3" />{interest.lender_company}</span>
                        )}
                      </div>
                      {interest.message && <p className="text-xs text-[#666] mt-1 italic">"{interest.message}"</p>}
                      <div className="text-[10px] text-[#bbb] mt-1">{interest.created_at ? new Date(interest.created_at).toLocaleString() : ""}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => handleRespond(interest.interest_id, "accept")}
                        disabled={responding === interest.interest_id}
                        data-testid={`accept-interest-${interest.interest_id}`}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-500/20 text-emerald-600 border border-emerald-200 text-xs font-medium hover:bg-emerald-500/30 transition-colors disabled:opacity-50">
                        {responding === interest.interest_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                        Grant Access
                      </button>
                      <button onClick={() => handleRespond(interest.interest_id, "reject")}
                        disabled={responding === interest.interest_id}
                        data-testid={`reject-interest-${interest.interest_id}`}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-500/15 text-red-600 border border-red-400/30 text-xs font-medium hover:bg-red-500/25 transition-colors disabled:opacity-50">
                        <UserX className="h-3.5 w-3.5" /> Decline
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Accepted / Active Interests */}
          {acceptedInterests.length > 0 && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-2">
                <Unlock className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Approved Lenders</span>
              </div>
              {acceptedInterests.map((interest, i) => (
                <motion.div key={interest.interest_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4" data-testid={`accepted-interest-${interest.interest_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      <span className="font-medium text-[#0A0A0A] text-sm">{interest.lender_name}</span>
                      {interest.lender_company && (
                        <span className="text-xs text-[#999] flex items-center gap-1"><Building2 className="h-3 w-3" />{interest.lender_company}</span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusColors[interest.status] || "bg-gray-100 text-gray-600"}`}>
                      {statusLabels[interest.status] || interest.status}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Declined interests */}
          {interests.filter(i => i.status === "rejected").length > 0 && (
            <div className="space-y-2 mt-4">
              <span className="text-[10px] font-bold text-[#bbb] uppercase tracking-wider">Declined</span>
              {interests.filter(i => i.status === "rejected").map((interest, i) => (
                <div key={interest.interest_id} className="rounded-xl border border-cream-warm bg-cream/50 p-3 opacity-60">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#888]">{interest.lender_name}</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-500 border border-red-200">Declined</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {interests.length === 0 && (
            <div className="text-center py-8 text-[#999]">
              <Users className="h-10 w-10 mx-auto mb-3 text-cream/20" />
              <p className="text-sm">No lender interest yet. Interest will appear here once lenders view your deal.</p>
            </div>
          )}
        </>
      )}

      {/* AI Matches View */}
      {activeView === "matches" && (
        <>
          {matches.length === 0 ? (
            <div className="text-center py-8 text-[#999]">
              <Users className="h-10 w-10 mx-auto mb-3 text-cream/20" />
              <p>No lender matches found for this deal yet.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#555]">{matches.length} matched lender{matches.length !== 1 ? "s" : ""}</span>
                {matches.some(m => m.score != null) && <span className="text-xs text-[#999]">Sorted by match score</span>}
              </div>
              {matches.map((m, i) => (
                <motion.div key={m.user_id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className={`rounded-xl border p-4 ${getScoreBg(m.score)}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-medium text-[#0A0A0A] text-sm">{m.full_name}</span>
                        {m.company && <span className="text-xs text-[#999] flex items-center gap-1"><Building2 className="h-3 w-3" />{m.company}</span>}
                        {m.already_interested && <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-600 border border-emerald-200">Interested</span>}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {m.location && <span className="text-xs text-[#888] flex items-center gap-1"><MapPin className="h-3 w-3" />{m.location}</span>}
                        {m.company_type && <span className="text-xs text-[#888] capitalize">{m.company_type.replace("_", " ")}</span>}
                      </div>
                      {m.specialties?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {m.specialties.map(s => (
                            <span key={s} className="px-2 py-0.5 rounded-md text-[10px] bg-gold/10 text-emerald-700/80 border border-cream-warm">{s}</span>
                          ))}
                        </div>
                      )}
                      {(m.reasons || []).length > 0 && (
                        <div className="mt-2 flex gap-1.5 flex-wrap">
                          {m.reasons.slice(0, 3).map((r, j) => (
                            <span key={j} className="text-[10px] text-[#999] flex items-center gap-1"><CheckCircle className="h-2.5 w-2.5 text-emerald-600/60" />{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {m.score != null && (
                      <div className="text-right shrink-0">
                        <div className={`text-2xl font-bold ${getScoreColor(m.score)}`}>{m.score}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#999]">Match</div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </>
      )}
    </motion.div>
  );
}
